import { useEffect, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, AlertTriangle, XCircle, Undo2, FilePlus2, Gavel, Loader2 } from "lucide-react";
import { Panel, Btn, RT } from "./ui";
import SignaturePad from "./SignaturePad";
import AiReviewPanel from "./AiReviewPanel";

const CERT = "I certify that the information contained within this report is true and accurate to the best of my knowledge and that the report accurately reflects the information known to me at the time of submission.";

export default function SectionApprovals({ rep, user, onChanged }) {
  const [val, setVal] = useState(null);
  const [certify, setCertify] = useState(false);
  const [sig, setSig] = useState("");
  const [supSig, setSupSig] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const isSup = ["supervisor", "watch_commander", "records_supervisor", "admin"].includes(user?.role);
  const isAuthor = rep.created_by_user_id === user?.id;
  useEffect(() => {
    client.get(`/rms-reports/${rep.id}/validate`).then((r) => setVal(r.data)).catch(() => {});
  }, [rep.id, rep.status]);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await client.post(`/rms-reports/${rep.id}/submit`, { certify, signature_image: sig });
      toast.success("Report submitted for supervisory review");
      if (r.data.warnings?.length) toast.warning(`${r.data.warnings.length} warning(s) noted for supervisor`);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); }
    setBusy(false);
  };
  const review = async (action) => {
    if (["return", "reassign", "refer"].includes(action) && !reason.trim()) {
      toast.error("A reason is required for this action"); return;
    }
    setBusy(true);
    try {
      await client.post(`/rms-reports/${rep.id}/review`, { action, reason: reason.trim(), signature_image: supSig });
      toast.success(`Report ${action === "approve" ? "APPROVED — original locked" : action.toUpperCase().replace("_", " ")}`);
      setReason(""); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Review action failed"); }
    setBusy(false);
  };

  return (
    <div className="space-y-3">
      <AiReviewPanel rep={rep} />
      <Panel title="Automated RMS Validation" testid="panel-validation">
        {!val ? <Loader2 size={14} className="animate-spin" style={{ color: RT.dim }} /> : (
          <div className="space-y-1">
            {val.errors.length === 0 && val.warnings.length === 0 && (
              <div className="flex items-center gap-2 font-mono text-[11px] text-[#15803d]"><ShieldCheck size={13} /> ALL VALIDATION CHECKS PASSED</div>)}
            {val.errors.map((e, i) => (
              <div key={i} data-testid={`val-error-${i}`} className="flex items-start gap-2 font-mono text-[11px] text-[#b91c1c]"><XCircle size={12} className="mt-0.5 shrink-0" /> {e}</div>))}
            {val.warnings.map((w, i) => (
              <div key={i} data-testid={`val-warning-${i}`} className="flex items-start gap-2 font-mono text-[11px] text-[#b45309]"><AlertTriangle size={12} className="mt-0.5 shrink-0" /> {w}</div>))}
          </div>
        )}
      </Panel>

      {["DRAFT", "RETURNED"].includes(rep.status) && (isAuthor || isSup) && (
        <Panel title="Report Certification & Submission" testid="panel-submit">
          {rep.status === "RETURNED" && rep.review?.reason && (
            <div className="mb-2 border-l-4 border-[#b91c1c] bg-[#fdf0f0] p-2 font-mono text-[11px] text-[#b91c1c]" data-testid="return-reason">
              RETURNED — CORRECTION REQUIRED: “{rep.review.reason}”
            </div>
          )}
          <label className="flex items-start gap-2 cursor-pointer">
            <input data-testid="certify-checkbox" type="checkbox" checked={certify} onChange={(e) => setCertify(e.target.checked)} className="mt-1 accent-[#1d5d90]" />
            <span className="text-[11.5px] italic" style={{ color: RT.text }}>“{CERT}”</span>
          </label>
          <div className="mt-3">
            <div className="font-mono text-[9px] uppercase mb-1" style={{ color: RT.dim }}>
              Officer signature — sign on-screen below (captured on the exported report)
            </div>
            <SignaturePad onChange={setSig} label="SIGN HERE — REPORTING OFFICER" />
          </div>
          <div className="mt-3">
            <Btn onClick={submit} disabled={!certify || busy || (val?.errors?.length > 0)} testid="submit-report-btn">
              <Gavel size={12} /> Submit for Supervisory Review
            </Btn>
            {val?.errors?.length > 0 && <span className="ml-2 font-mono text-[10px] text-[#b91c1c]">Resolve validation errors first.</span>}
          </div>
        </Panel>
      )}

      {rep.certification && (
        <Panel title="Certification Record" testid="panel-cert-record">
          {rep.certification.signature_image
            ? <img src={rep.certification.signature_image} alt="Officer signature" data-testid="officer-signature-img" className="h-14 mb-1" />
            : <div data-testid="officer-signature" className="text-[30px] leading-none mb-1"
                style={{ fontFamily: "'Brush Script MT', 'Segoe Script', 'Lucida Handwriting', cursive", color: "#1a2f6e" }}>
                {rep.certification.signature || rep.certification.name}
              </div>}
          <div className="border-t pt-1 font-mono text-[9px] uppercase" style={{ borderColor: RT.border, color: RT.dim }}>
            Signature of Reporting Officer · Date: {rep.certification.date || rep.certification.ts?.slice(0, 10)}
          </div>
          <div className="font-mono text-[11px] mt-2" style={{ color: RT.text }}>
            CERTIFIED BY: {rep.certification.name} · EMP ID {rep.certification.employee_id} · {rep.certification.ts?.slice(0, 16)} UTC
            <div style={{ color: RT.dim }}>AUTHENTICATION: {rep.certification.authentication}</div>
          </div>
        </Panel>
      )}

      {isSup && rep.status === "PENDING REVIEW" && (
        <Panel title="Supervisory Review" testid="panel-review">
          <input data-testid="review-reason" value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (required for Return / Reassign / Refer)"
            className="w-full border px-2 py-1.5 text-[12px] font-mono rounded-sm mb-2"
            style={{ background: RT.input, borderColor: RT.border, color: RT.text }} />
          <div className="mb-2">
            <div className="font-mono text-[9px] uppercase mb-1" style={{ color: RT.dim }}>
              Supervisor signature — sign on-screen below (captured on the exported report)
            </div>
            <SignaturePad onChange={setSupSig} label="SIGN HERE — APPROVING SUPERVISOR" />
          </div>
          <div className="flex flex-wrap gap-2">
            <Btn onClick={() => review("approve")} color="#15803d" disabled={busy} testid="review-approve"><ShieldCheck size={12} /> Approve</Btn>
            <Btn onClick={() => review("return")} color="#b91c1c" disabled={busy} testid="review-return"><Undo2 size={12} /> Return for Correction</Btn>
            <Btn onClick={() => review("request_supplement")} color="#b45309" disabled={busy} testid="review-supplement"><FilePlus2 size={12} /> Request Supplement</Btn>
            <Btn onClick={() => review("refer")} color="#7c3aed" disabled={busy} testid="review-refer">Refer for Investigation</Btn>
          </div>
        </Panel>
      )}

      {rep.review && rep.status !== "PENDING REVIEW" && (
        <Panel title="Review Record" testid="panel-review-record">
          {rep.review.signature_image && <img src={rep.review.signature_image} alt="Supervisor signature" data-testid="supervisor-signature-img" className="h-12 mb-1" />}
          <div className="font-mono text-[11px]" style={{ color: RT.text }}>
            {rep.review.action} by {rep.review.rank} {rep.review.reviewed_by} · {rep.review.reviewed_at?.slice(0, 16)} UTC
            {rep.review.reason ? <div style={{ color: RT.dim }}>REASON: {rep.review.reason}</div> : null}
          </div>
        </Panel>
      )}
    </div>
  );
}
